/**
 * @fileoverview Organizer menu component.
 * @summary Dropdown menu for organizers with navigation options.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/OrganizerMenu
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux.js'
import { logout } from '../../../store/slices/authSlice.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBars,
  faGauge,
  faMapMarkerAlt,
  faCog,
  faCircleInfo,
  faRightFromBracket,
  faShieldAlt,
  faDollarSign,
  // faChevronDown,
} from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Menu item interface.
 */
interface MenuItem {
  id: string
  label: string
  icon: typeof faGauge
  path?: string
  action?: () => void
  divider?: boolean
}

/**
 * @summary Organizer menu component.
 * @returns {React.ReactElement} Organizer menu component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function OrganizerMenu() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { user } = useAppSelector((state) => state.auth)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap()
      navigate('/login', { replace: true })
    } catch (error) {
      // Even if logout fails, redirect to login
      navigate('/login', { replace: true })
    } finally {
      setIsOpen(false)
    }
  }

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: t('organizerMenu.dashboard'),
      icon: faGauge,
      path: '/dashboard',
    },
    {
      id: 'venues',
      label: t('organizerMenu.venues'),
      icon: faMapMarkerAlt,
      path: '/venues',
    },
    {
      id: 'settings',
      label: t('organizerMenu.settings'),
      icon: faCog,
      path: '/settings/organizer',
    },
    {
      id: 'data-privacy',
      label: t('organizerMenu.dataPrivacy'),
      icon: faShieldAlt,
      path: '/data-privacy',
    },
    {
      id: 'subscription',
      label: t('organizerMenu.subscriptions'),
      icon: faDollarSign,
      path: '/subscription',
    },
    {
      id: 'about',
      label: t('organizerMenu.about'),
      icon: faCircleInfo,
      path: '/about',
    },
    {
      id: 'divider',
      label: '',
      icon: faBars,
      divider: true,
    },
    {
      id: 'logout',
      label: t('auth.logout'),
      icon: faRightFromBracket,
      action: handleLogout,
    },
  ]

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.action) {
      item.action()
    } else if (item.path) {
      navigate(item.path)
      setIsOpen(false)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Don't render if user is not an organizer
  if (!user || (user.role !== 'organizer' && user.role !== 'superadmin')) {
    return null
  }

  // const displayName = user.profile?.firstName || user.email || t('organizerMenu.user')

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
        aria-label={t('organizerMenu.menu')}
        title={t('organizerMenu.menu')}
      >
        {/* Burger icon for mobile */}
        <FontAwesomeIcon icon={faBars} className="w-5 h-5" />
        {/* Name and chevron for desktop */}
        {/* <span className="hidden lg:inline text-sm font-medium whitespace-nowrap">
          {displayName}
        </span> */}
        {/* <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`hidden lg:block w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        /> */}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-60" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-70 mt-2 w-48 bg-(--color-bg-secondary) rounded-lg shadow-2xl ">
            <div className="py-1">
              {menuItems.map((item) => {
                if (item.divider) {
                  return (
                    <div
                      key={item.id}
                      className="my-1 border-t border-(--color-border)"
                    />
                  )
                }
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleMenuItemClick(item)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-(--color-text-secondary) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary) transition-colors"
                  >
                    <FontAwesomeIcon icon={item.icon} className="w-4 h-4" />
                    <span className="text-left">{item.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

